import React, { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronRight, Menu, X } from 'lucide-react';
import { colors } from '@/design-system/colors';
import { navigationItems, filterNavigation } from './navigation-items';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useNavigationState } from './use-navigation-state';
import { CompanyLogoBox } from './CompanyLogoBox';
import { CompanySwitcher } from './CompanySwitcher';

interface MobileNavigationProps {
  onNavigate: (path: string) => void;
  onAIChatOpen?: () => void;
}

// BUG-16 (2026-09-19): below `md` (see tailwind.config.js), sidebar-navigation.tsx
// renders nothing at all (`hidden md:flex`) -- previously there was no
// replacement, so there was literally no way to navigate the app on a
// phone/small-tablet screen except editing the URL. This is that
// replacement: a hamburger trigger (visible only `md:hidden`, so it
// never doubles up with the desktop Sidebar) opening an off-canvas
// drawer built from the exact same navigationItems/useNavigationState
// the desktop Sidebar uses, so the two can never drift apart.
export function MobileNavigation({ onNavigate, onAIChatOpen }: MobileNavigationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { can } = usePermissions();
  // and only what the navigation matrix of the role (in this company) leaves ON
  const { isVisibleKey } = useNavigation();
  const visibleItems = useMemo(() => filterNavigation(navigationItems, can, isVisibleKey), [can, isVisibleKey]);
  const { openMenus, handleItemClick, handleSubmenuClick } = useNavigationState({
    onNavigate,
    onAIChatOpen,
    onAfterAction: () => setIsOpen(false),
  });

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="md:hidden flex-shrink-0 rounded-lg p-2 hover:bg-white/10 transition-colors"
          aria-label="Open navigation menu"
          style={{ color: colors.buttonTextWhite }}
        >
          <Menu className="h-6 w-6" />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 md:hidden" />
        <Dialog.Content
          className="fixed inset-y-0 left-0 z-50 h-full w-sidebar max-w-[85vw] flex flex-col outline-none md:hidden"
          style={{ backgroundColor: colors.sidebar }}
          aria-describedby={undefined}
        >
          <div className="flex items-center justify-between p-4 flex-shrink-0">
            <Dialog.Title className="text-sm font-semibold" style={{ color: colors.sidebarText }}>
              Menu
            </Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" aria-label="Close navigation menu" className="rounded-lg p-1 hover:bg-white/10 transition-colors">
                <X className="h-5 w-5" style={{ color: colors.sidebarText }} />
              </button>
            </Dialog.Close>
          </div>
          <div className="flex flex-col gap-2 p-4 pt-0 overflow-y-auto">
            <CompanyLogoBox />
            <CompanySwitcher />
            {visibleItems.map((item) => (
              <div key={item.title} className="relative">
                <button
                  onClick={() => handleItemClick(item)}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 nav-header transition-colors"
                  style={{
                    backgroundColor: openMenus.includes(item.title) && item.submenu ? colors.sidebarActive : 'transparent',
                    color: colors.sidebarText,
                  }}
                >
                  <div className="flex items-center min-w-0">
                    {React.cloneElement(item.icon as React.ReactElement, { className: 'mr-2 h-4 w-4 flex-shrink-0', style: { color: colors.sidebarText } })}
                    <span className="nav-header truncate" style={{ color: colors.sidebarText }}>{item.title}</span>
                  </div>
                  {item.submenu && (
                    openMenus.includes(item.title) ?
                      <ChevronRight className="h-4 w-4 flex-shrink-0 rotate-90" style={{ color: colors.sidebarText }} /> :
                      <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: colors.sidebarText }} />
                  )}
                </button>

                {item.submenu && openMenus.includes(item.title) && (
                  <div className="pl-6 pt-1 pb-1">
                    <div className="flex flex-col gap-1 rounded-md p-2 mt-1" style={{ backgroundColor: colors.sidebar }}>
                      {item.submenu.map((subItem) => (
                        <button
                          key={subItem.title}
                          onClick={(e) => handleSubmenuClick(e, subItem, item.title)}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 nav-subheader justify-start transition-colors min-w-0"
                          style={{ color: colors.sidebarText }}
                        >
                          {React.cloneElement(subItem.icon as React.ReactElement, { className: 'h-4 w-4 flex-shrink-0', style: { color: colors.sidebarText } })}
                          <span className="nav-subheader truncate" style={{ color: colors.sidebarText }}>{subItem.title}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
